import type { AkimaConfig, AkimaLabels } from "./types";
import { DEFAULT_ENDPOINT, DEFAULT_LABELS } from "./types";
import { normalizeUrl, validateUrl, validateEmail } from "./validation";
import { submitAssessment } from "./api";

const STYLE_ID = "akima-sdk-default-styles";
const DEFAULT_STYLES = `
.akima-form {
  --akima-form-accent: #0079b0;
  --akima-form-accent-dark: #00618d;
  --akima-form-text: #111827;
  --akima-form-muted: #4b5563;
  --akima-form-border: #d8e3ea;
  --akima-form-surface: #f7f9fb;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 720px;
  margin: 0;
  color: var(--akima-form-text);
  font-family: inherit;
  line-height: 1.45;
}

.akima-form *,
.akima-form *::before,
.akima-form *::after {
  box-sizing: border-box;
}

.akima-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.akima-label {
  display: block;
  margin: 0;
  color: #000000;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
}

.akima-form input.akima-input {
  width: 100%;
  min-height: 54px;
  margin: 0;
  padding: 0 18px;
  border: 1px solid var(--akima-form-border);
  border-radius: 4px;
  color: #111827 !important;
  background-color: var(--akima-form-surface);
  caret-color: #111827;
  font: inherit;
  font-size: 16px;
  line-height: 1.4;
  box-shadow: none;
  transition: border-color 160ms ease, box-shadow 160ms ease, background-color 160ms ease;
}

.akima-form input.akima-input::placeholder {
  color: #6b7280;
  opacity: 1;
}

.akima-form input.akima-input:focus {
  outline: none;
  border-color: var(--akima-form-accent);
  background-color: #ffffff;
  box-shadow: 0 0 0 3px rgba(0, 121, 176, 0.14);
}

.akima-error {
  min-height: 0;
  color: #b42318;
  font-size: 14px;
  line-height: 1.4;
}

.akima-consent {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.akima-consent + .akima-consent {
  margin-top: -4px;
}

.akima-consent-label {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr);
  gap: 12px;
  align-items: flex-start;
  margin: 0;
  color: var(--akima-form-text);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
}

.akima-consent-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 3px 0 0;
  flex: 0 0 auto;
  accent-color: var(--akima-form-accent);
}

.akima-consent-label span {
  min-width: 0;
}

.akima-consent-label a {
  color: var(--akima-form-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.akima-consent-hint {
  margin: 0 0 0 30px;
  color: var(--akima-form-muted);
  font-size: 15px;
  line-height: 1.55;
}

.akima-submit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  min-height: 52px;
  margin: 4px 0 0;
  padding: 0 30px;
  border: 0;
  border-radius: 999px;
  background: var(--akima-form-accent);
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  text-decoration: none;
  transition: background-color 160ms ease, transform 160ms ease, opacity 160ms ease;
}

.akima-submit:hover:not(:disabled),
.akima-submit:focus-visible:not(:disabled) {
  background: var(--akima-form-accent-dark);
}

.akima-submit:focus-visible {
  outline: 3px solid rgba(0, 121, 176, 0.22);
  outline-offset: 3px;
}

.akima-submit:active:not(:disabled) {
  transform: translateY(1px);
}

.akima-submit:disabled {
  cursor: not-allowed;
  opacity: 0.62;
}

.akima-status {
  color: var(--akima-form-muted);
  font-size: 15px;
  line-height: 1.45;
}

.akima-status--error {
  padding: 12px 14px;
  border: 1px solid #fecdca;
  border-radius: 6px;
  background: #fffbfa;
  color: #b42318;
}

.akima-success {
  padding: 18px 20px;
  border: 1px solid #b7e4d7;
  border-radius: 6px;
  background: #f2fbf8;
  color: var(--akima-form-text);
}

.akima-success-title {
  margin-bottom: 6px;
  color: #0f4337;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
}

.akima-success-message {
  color: var(--akima-form-muted);
  font-size: 16px;
  line-height: 1.5;
}

@media (max-width: 640px) {
  .akima-form {
    gap: 16px;
  }

  .akima-label {
    font-size: 16px;
  }

  .akima-form input.akima-input {
    min-height: 50px;
    padding: 0 15px;
  }

  .akima-consent-label {
    font-size: 15px;
    gap: 10px;
  }

  .akima-consent-hint {
    margin-left: 28px;
    font-size: 14px;
  }

  .akima-submit {
    width: 100%;
    min-height: 50px;
  }
}
`;

function ensureDefaultStyles(): void {
  if (document.getElementById(STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = DEFAULT_STYLES;
  document.head.appendChild(style);
}

export function renderForm(config: AkimaConfig): void {
  const container =
    typeof config.container === "string"
      ? document.querySelector<HTMLElement>(config.container)
      : config.container;

  if (!container) {
    console.error("[AkimaSDK] Container not found:", config.container);
    return;
  }

  const labels: AkimaLabels = { ...DEFAULT_LABELS, ...config.labels };
  const endpoint = config.endpoint || DEFAULT_ENDPOINT;

  ensureDefaultStyles();

  // Build form HTML
  container.innerHTML = `
<form class="akima-form" novalidate>
  <div class="akima-field">
    <label class="akima-label" for="akima-url">${labels.url}</label>
    <input class="akima-input" id="akima-url" type="text" inputmode="url"
           autocomplete="url" name="url" placeholder="beispiel.de" required />
    <div class="akima-error" role="alert" aria-live="polite"></div>
  </div>
  <div class="akima-field">
    <label class="akima-label" for="akima-email">${labels.email}</label>
    <input class="akima-input" id="akima-email" type="email" name="email"
           placeholder="name@firma.de" required />
    <div class="akima-error" role="alert" aria-live="polite"></div>
  </div>
  <div class="akima-consent">
    <label class="akima-consent-label" for="akima-privacy">
      <input type="checkbox" id="akima-privacy" name="privacyConsent" />
      <span>(Pflichtfeld) Ich habe die <a href="https://akima.de/de/datenschutz/" target="_blank" rel="noopener noreferrer">Datenschutzhinweise</a> gelesen und stimme der Verarbeitung meiner Daten zur Bearbeitung dieser Anfrage zu.</span>
    </label>
    <div class="akima-consent-hint">Ihre Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet und nicht an Dritte weitergegeben. Dieses Einverständnis können Sie jederzeit widerrufen.</div>
    <div class="akima-error" role="alert" aria-live="polite"></div>
  </div>
  <div class="akima-consent">
    <label class="akima-consent-label" for="akima-email-consent">
      <input type="checkbox" id="akima-email-consent" name="emailFollowUpConsent" />
      <span>(Pflichtfeld) Ich stimme der Kontaktaufnahme per E-Mail im Zusammenhang mit meiner Anfrage zu (z.&nbsp;B. Rückfragen, Terminvereinbarung für ein Erstgespräch).</span>
    </label>
    <div class="akima-error" role="alert" aria-live="polite"></div>
  </div>
  <button class="akima-submit" type="submit" disabled>${labels.submit}</button>
  <div class="akima-status" role="status" aria-live="polite"></div>
</form>`;

  const form = container.querySelector<HTMLFormElement>(".akima-form")!;
  const urlInput = form.querySelector<HTMLInputElement>("#akima-url")!;
  const emailInput = form.querySelector<HTMLInputElement>("#akima-email")!;
  const privacyCheckbox = form.querySelector<HTMLInputElement>("#akima-privacy")!;
  const emailConsentCheckbox =
    form.querySelector<HTMLInputElement>("#akima-email-consent")!;
  const urlError = urlInput
    .closest(".akima-field")!
    .querySelector<HTMLElement>(".akima-error")!;
  const emailError = emailInput
    .closest(".akima-field")!
    .querySelector<HTMLElement>(".akima-error")!;
  const privacyError = privacyCheckbox
    .closest(".akima-consent")!
    .querySelector<HTMLElement>(".akima-error")!;
  const emailConsentError = emailConsentCheckbox
    .closest(".akima-consent")!
    .querySelector<HTMLElement>(".akima-error")!;
  const submitBtn = form.querySelector<HTMLButtonElement>(".akima-submit")!;
  const status = form.querySelector<HTMLElement>(".akima-status")!;

  function updateSubmitState(): void {
    submitBtn.disabled =
      !privacyCheckbox.checked || !emailConsentCheckbox.checked;
  }

  privacyCheckbox.addEventListener("change", updateSubmitState);
  emailConsentCheckbox.addEventListener("change", updateSubmitState);
  updateSubmitState();

  function clearErrors(): void {
    urlError.textContent = "";
    emailError.textContent = "";
    privacyError.textContent = "";
    emailConsentError.textContent = "";
    status.textContent = "";
    status.className = "akima-status";
  }

  form.addEventListener("submit", async (e: Event) => {
    e.preventDefault();
    clearErrors();

    const rawUrl = urlInput.value.trim();
    const normalizedUrl = normalizeUrl(rawUrl);
    const urlVal = validateUrl(rawUrl);
    const emailVal = validateEmail(emailInput.value.trim());

    if (urlVal) {
      urlError.textContent = urlVal.message;
      urlInput.focus();
      return;
    }
    if (emailVal) {
      emailError.textContent = emailVal.message;
      emailInput.focus();
      return;
    }
    if (!privacyCheckbox.checked) {
      privacyError.textContent = "Bitte stimmen Sie den Datenschutzhinweisen zu.";
      privacyCheckbox.focus();
      return;
    }
    if (!emailConsentCheckbox.checked) {
      emailConsentError.textContent =
        "Bitte stimmen Sie der Kontaktaufnahme per E-Mail zu.";
      emailConsentCheckbox.focus();
      return;
    }

    // Disable form during submission
    submitBtn.disabled = true;
    submitBtn.textContent = labels.submitting;

    try {
      const result = await submitAssessment({
        url: normalizedUrl,
        email: emailInput.value.trim(),
        endpoint,
        source: config.source,
        privacyConsent: true,
        emailFollowUpConsent: true,
      });

      // Show success
      form.innerHTML = `
<div class="akima-success">
  <div class="akima-success-title">${labels.successTitle}</div>
  <div class="akima-success-message">${labels.successMessage}</div>
</div>`;

      if (config.onSuccess) {
        config.onSuccess(result);
      }
    } catch (err) {
      updateSubmitState();
      submitBtn.textContent = labels.submit;
      const error =
        err && typeof err === "object" && "message" in err
          ? (err as { message: string })
          : { message: "Ein unerwarteter Fehler ist aufgetreten." };
      status.textContent = error.message;
      status.className = "akima-status akima-status--error";

      if (config.onError) {
        config.onError(error);
      }
    }
  });
}
